import { useState, type FormEvent } from 'react';
import { INTERVAL_OPTIONS } from '../lib/intervals';
import type { MarketInterval } from '../types/market';

export type AppView = 'dashboard' | 'scanner' | 'alerts';

interface HeaderProps {
  interval: MarketInterval;
  onIntervalChange: (interval: MarketInterval) => void;
  onAddSymbol: (symbol: string) => boolean;
  onRefresh: () => void;
  symbolCount: number;
  maxSymbols: number;
  view: AppView;
  onViewChange: (view: AppView) => void;
  alertCount: number;
  bridgeStatus: 'connected' | 'missing' | 'checking';
  bridgeVersion?: string;
  onCheckBridge: () => void;
  onOpenPermissions: () => void;
  canInstallPwa: boolean;
  pwaInstalled: boolean;
  onInstallPwa: () => void;
  online: boolean;
  pollMs: number;
  onPollMsChange: (value: number) => void;
  autoRefresh: boolean;
  onAutoRefreshChange: (value: boolean) => void;
}

export function Header({ 
  interval, onIntervalChange, onAddSymbol, onRefresh, symbolCount, maxSymbols, view, onViewChange, alertCount,
  bridgeStatus, bridgeVersion, onCheckBridge, onOpenPermissions, canInstallPwa, pwaInstalled, onInstallPwa, online, pollMs, onPollMsChange, autoRefresh, onAutoRefreshChange
}: HeaderProps) {
  const [input, setInput] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();
    if (onAddSymbol(input)) setInput('');
  }

  return (
    <header className="topbar">
      <div className="brand-and-nav">
        <div className="brand-block">
          <div className="brand-mark">GMV</div>
          <div>
            <h1>GoldMine Vision</h1>
            <div className="brand-subtitle"><span className="provider-dot" /> See the Signal · Rust/WASM</div>
          </div>
        </div>
        <nav className="primary-nav" aria-label="Primary navigation">
          <button className={view === 'dashboard' ? 'active' : ''} type="button" onClick={() => onViewChange('dashboard')}>Dashboard</button>
          <button className={view === 'scanner' ? 'active' : ''} type="button" onClick={() => onViewChange('scanner')}>Scanner</button>
          <button className={view === 'alerts' ? 'active' : ''} type="button" onClick={() => onViewChange('alerts')}>Alerts{alertCount ? <span>{alertCount}</span> : null}</button>
        </nav>
      </div>
      <div id="header-chart-controls" style={{ display: 'flex', flex: 1, margin: '0 16px', alignItems: 'center' }} />

      <div className="toolbar" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#b2b5be' }} title={bridgeStatus === 'connected' ? `Bridge v${bridgeVersion}` : 'Bridge missing'}>
          <span className={`status-dot ${bridgeStatus}`} style={{ width: '8px', height: '8px', borderRadius: '50%', background: bridgeStatus === 'connected' ? '#26a69a' : '#ef5350' }} />
          {online ? 'Online' : 'Offline'}
        </div>
        
        <div className="integration-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label className="compact-control" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>Auto
            <input type="checkbox" checked={autoRefresh} onChange={(event) => onAutoRefreshChange(event.target.checked)} />
          </label>
          <label className="compact-control" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
            <select value={pollMs} onChange={(event) => onPollMsChange(Number(event.target.value))} disabled={!autoRefresh} style={{ background: 'transparent', border: 'none', color: '#d1d4dc', fontSize: '12px' }}>
              <option value={15000}>15s</option>
              <option value={30000}>30s</option>
              <option value={60000}>60s</option>
              <option value={120000}>2m</option>
            </select>
          </label>
          {bridgeStatus === 'connected' ? <button type="button" className="action-link" style={{ background: 'none', border: 'none', color: '#4cc9f0', cursor: 'pointer', fontSize: '12px' }} onClick={onOpenPermissions}>API</button> : null}
          {bridgeStatus !== 'connected' ? (
            <button type="button" className="action-link" style={{ background: 'none', border: 'none', color: '#ef5350', cursor: 'pointer', fontSize: '12px' }} onClick={onCheckBridge}>Check Bridge</button>
          ) : null}
          {canInstallPwa ? (
            <button type="button" className="action-link primary" style={{ background: '#26a69a', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '12px', padding: '4px 8px', borderRadius: '4px' }} onClick={onInstallPwa}>Install PWA</button>
          ) : null}
        </div>

        <button className="refresh-button" type="button" onClick={onRefresh} style={{ marginLeft: '8px' }}>Refresh</button>
      </div>
    </header>
  );
}
